import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";

import type { FormikInitialValuesNewRequest } from "../../model/form.model";
import { useNewRequestContext } from "../../state/NewRequestProvider";

import { calculateOrderTotals } from "../../domain/calculations/orderTotals";
import { validateOrder } from "../../domain/validators/validateOrder";
import { useNewRequestController } from "./useNewRequestController";
import { formatBRL } from "@utils/currency";

export function useReviewStep() {
  const { action: actionNewRequestController } = useNewRequestController();

  const { state } = useNewRequestContext();

  const formik = useFormikContext<FormikInitialValuesNewRequest>();

  const cart = state.products.cart;
  const pricingStatus = state.products.pricingStatus;
  const customerSelected = Boolean(state.purchase.customer?.id);

  const localTotals = useMemo(() => calculateOrderTotals(cart), [cart]);

  const totals = useMemo(
    () => ({
      totalQuantity: localTotals.totalQuantity,
      totalPrice: state.products.totalPrice,
      hasFetchedTotal: state.products.totalPrice !== null,
    }),
    [localTotals.totalQuantity, state.products.totalPrice]
  );

  const validation = useMemo(
    () =>
      validateOrder({
        purchase: formik.values.purchase,
        cart,
        pricingStatus,
        customerSelected,
        customerDetails: state.purchase.customer,
      }),
    [
      formik.values.purchase,
      cart,
      pricingStatus,
      customerSelected,
      state.purchase.customer,
    ]
  );

  const isSubmitting = formik.isSubmitting;
  const canSubmit = validation.ok && !isSubmitting;

  const formattedTotalPrice = useMemo(() => {
    if (totals.hasFetchedTotal) return formatBRL(totals.totalPrice!);
    if (pricingStatus === "error") return "Erro ao calcular preço";
    if (pricingStatus === "loading") return "Calculando...";
    return formatBRL(0);
  }, [totals.hasFetchedTotal, totals.totalPrice, pricingStatus]);

  return {
    state: {
      cart,
      totals,
      validation,
      canSubmit,
      isSubmitting,
      pricingStatus,
      formattedTotalPrice,
    },
    action: {
      reset: useCallback(() => actionNewRequestController.resetFlow(formik), [
        actionNewRequestController,
        formik,
      ]),
    },
  };
}

export function paymentLabel(payment: "T" | "B", deadline: number) {
  if (payment === "T") return "À vista";
  const d = Number(deadline) || 0;
  return d <= 0 ? "À vista" : `${d} ${d === 1 ? "Dia" : "Dias"}`;
}
